import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarkdownRenderer from "@/components/elements/markdown-renderer";
import StepGroups from "@/components/pages/settings-panels/trace-steps";
import { useUser } from "@/hooks/use-user";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type { ProcessingStatus } from "@/types/chat";
import {
  RunHistoryError,
  formatCost,
  formatDuration,
  formatTokens,
  shortModel,
  useChatUsage,
  useChatUsageDetails,
  type ChatUsageFilters,
  type ChatUsageMode,
  type ChatUsageRow,
} from "@/service-hooks/use-run-history";

// Settings → Runs & Cost → Chats: one row per chat exchange (the question and its answer), normal and deep, with what
// answering cost; opening a row shows the whole texts and the steps taken (tools, model calls, errors). Platform admins
// see every project, project owners the projects they own (the API decides).

const PAGE_SIZE = 20;

const isoDay = (d: Date) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const SORTS: { value: NonNullable<ChatUsageFilters["sortName"]>; label: string }[] = [
  { value: "createdAt", label: "Newest first" },
  { value: "cost", label: "Highest cost" },
  { value: "tokens", label: "Most tokens" },
  { value: "seconds", label: "Longest" },
];

const MODES: { value: "" | ChatUsageMode; label: string }[] = [
  { value: "", label: "Normal and deep" },
  { value: "normal", label: "Normal" },
  { value: "deep", label: "Deep analysis" },
];

const STATUSES: { value: "" | ProcessingStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "no_answer", label: "No answer" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_STYLE: Record<string, string> = {
  completed: "bg-success/15 text-success",
  processing: "bg-warning/15 text-warning",
  failed: "bg-error/15 text-error",
  no_answer: "bg-surface-hover text-muted",
  cancelled: "bg-surface-hover text-muted",
};

const fieldClass =
  "h-8 rounded-md bg-surface-active text-white text-xs px-2 border border-surface-border focus:outline-none focus:ring-1 focus:ring-primary";

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

export default function ChatUsageView() {
  const [mode, setMode] = useState<"" | ChatUsageMode>("");
  const [projectUuid, setProjectUuid] = useState("");
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [sort, setSort] = useState<NonNullable<ChatUsageFilters["sortName"]>>("createdAt");
  const [status, setStatus] = useState<"" | ProcessingStatus>("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);

  const { data, isLoading, isFetching, error } = useChatUsage({
    mode: mode || undefined,
    projectUuid: projectUuid || undefined,
    from: from || undefined,
    to: to || undefined,
    sortName: sort,
    status: status || undefined,
    page,
    limit: PAGE_SIZE,
  });
  const forbidden = error instanceof RunHistoryError && error.status === 403;
  const isAdmin = useUser().user?.role === "ADMIN";
  const totals = data?.totals;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const resetPage = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  if (forbidden) {
    return (
      <div className="bg-surface-card border border-surface-border rounded-lg p-4 flex items-start gap-3">
        <div className="p-2 bg-warning/20 rounded flex-shrink-0">
          <Lock className="w-5 h-5 text-warning" />
        </div>
        <div className="min-w-0">
          <h2 className="text-xs font-semibold text-white">No chats to show</h2>
          <p className="text-xs text-muted mt-1">
            Chats and their cost are shown to platform admins, and to project owners for the projects they own.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="bg-surface-card border border-surface-border rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted">
          Project
          <select
            aria-label="Project"
            className={cn(fieldClass, "min-w-[200px]")}
            value={projectUuid}
            onChange={(e) => resetPage(setProjectUuid)(e.target.value)}
          >
            <option value="">All projects</option>
            {(data?.projects ?? []).map((p) => (
              <option key={p.uuid} value={p.uuid}>
                {p.name ?? p.uuid}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">
          Mode
          <select aria-label="Mode" className={cn(fieldClass, "min-w-[150px]")} value={mode}
            onChange={(e) => resetPage(setMode)(e.target.value as typeof mode)}>
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">
          From
          <input type="date" aria-label="From" className={fieldClass} value={from} max={to || undefined}
            onChange={(e) => resetPage(setFrom)(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted">
          To
          <input type="date" aria-label="To" className={fieldClass} value={to} min={from || undefined}
            onChange={(e) => resetPage(setTo)(e.target.value)} />
        </label>
        <div className="flex shrink-0 items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Sort
            <select aria-label="Sort" className={cn(fieldClass, "min-w-[140px]")} value={sort}
              onChange={(e) => resetPage(setSort)(e.target.value as typeof sort)}>
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Status
            <select aria-label="Status" className={cn(fieldClass, "min-w-[140px]")} value={status}
              onChange={(e) => resetPage(setStatus)(e.target.value as typeof status)}>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <Card label="Total cost" value={formatCost(totals?.cost)} loading={isLoading} />
        <Card
          label={mode === "deep" ? "Deep chats" : mode === "normal" ? "Normal chats" : "Chats"}
          value={totals ? String(totals.chats) : "—"}
          hint={totals && totals.notAnswered ? `${totals.notAnswered} not answered` : undefined}
          loading={isLoading}
        />
        <Card
          label="Average per chat"
          value={totals && totals.chats ? formatCost(totals.cost / totals.chats) : "—"}
          hint={totals && totals.chats ? `${formatDuration(totals.seconds / totals.chats)} on average` : undefined}
          loading={isLoading}
        />
        <Card
          label="Tokens"
          value={totals ? formatTokens(totals.tokens.input + totals.tokens.output) : "—"}
          hint={
            totals
              ? `${formatTokens(totals.tokens.input)} in${totals.tokens.cached ? ` (${formatTokens(totals.tokens.cached)} cached)` : ""} · ${formatTokens(totals.tokens.output)} out`
              : undefined
          }
          loading={isLoading}
        />
      </div>

      <div className="bg-surface-card border border-surface-border rounded-lg overflow-hidden flex flex-col">
        <div className={`overflow-auto ${CUSTOM_SCROLLBAR}`}>
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-surface-hover text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-3 py-3 font-medium w-6" />
                <th className="px-3 py-3 font-medium">When</th>
                <th className="px-3 py-3 font-medium">Project</th>
                <th className="px-3 py-3 font-medium">Asked by</th>
                <th className="px-3 py-3 font-medium">Question</th>
                <th className="px-3 py-3 font-medium">Mode</th>
                <th className="px-3 py-3 font-medium">Model</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium text-right">Input</th>
                <th className="px-3 py-3 font-medium text-right">Output</th>
                <th className="px-3 py-3 font-medium text-right">Time</th>
                <th className="px-3 py-3 font-medium text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={12} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      <p className="text-muted text-xs">Loading chats…</p>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-error">
                    {(error as Error).message}
                  </td>
                </tr>
              ) : !data?.data.length ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-muted">
                    No {mode === "deep" ? "deep " : mode === "normal" ? "normal " : ""}chats in this period.
                    {!isAdmin && (
                      <p className="mt-1 text-xs">
                        Only chats in projects you own are shown (projects you created, or where you are an Owner).
                      </p>
                    )}
                  </td>
                </tr>
              ) : (
                data.data.map((row) => (
                  <ChatRow key={row.id} row={row} open={open === row.id} onToggle={() => setOpen(open === row.id ? null : row.id)} />
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-surface-border text-xs text-muted">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of {data.total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 bg-surface border-surface-border text-white"
                disabled={page <= 1 || isFetching} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span>
                Page {page} of {pages}
              </span>
              <Button variant="outline" size="sm" className="h-7 bg-surface border-surface-border text-white"
                disabled={page >= pages || isFetching} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, hint, loading }: { label: string; value: string; hint?: string; loading?: boolean }) {
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted">{label}</p>
      <p className="text-xl font-semibold text-white mt-1 tabular-nums">
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : value}
      </p>
      {hint && <p className="text-[11px] text-muted mt-1 truncate" title={hint}>{hint}</p>}
    </div>
  );
}

function ChatRow({ row, open, onToggle }: { row: ChatUsageRow; open: boolean; onToggle: () => void }) {
  return (
    <Fragment>
      <tr className="border-t border-surface-border hover:bg-surface-hover cursor-pointer" onClick={onToggle} aria-expanded={open}>
        <td className="px-3 py-2 text-muted">{open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
        <td className="px-3 py-2 whitespace-nowrap text-white">{when(row.at)}</td>
        <td className="px-3 py-2 text-white">{row.project.name ?? <span className="text-muted">unknown</span>}</td>
        <td className="px-3 py-2 text-muted" title={row.user.email ?? undefined}>{row.user.name || row.user.email || "—"}</td>
        <td className="px-3 py-2 text-white max-w-[360px] truncate" title={row.question ?? ""}>
          {row.question ?? "—"}
          {row.thread?.title && <span className="block text-xs text-muted truncate">{row.thread.title}</span>}
        </td>
        <td className="px-3 py-2">
          <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap",
            row.mode === "deep" ? "bg-primary/20 text-primary" : "bg-surface-hover text-muted")}>
            {row.mode === "deep" ? "deep" : "normal"}
          </span>
        </td>
        <td className="px-3 py-2 text-muted" title={row.model ?? undefined}>{shortModel(row.model)}</td>
        <td className="px-3 py-2">
          <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium", STATUS_STYLE[row.status ?? ""] ?? "bg-surface-hover text-muted")}>
            {row.status === "no_answer" ? "no answer" : (row.status ?? "—")}
          </span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted"
          title={`${row.tokens.input.toLocaleString()} input tokens${row.tokens.cached ? ` (${row.tokens.cached.toLocaleString()} cached)` : ""}`}>
          {formatTokens(row.tokens.input)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-muted">{formatTokens(row.tokens.output)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-muted">{formatDuration(row.seconds)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-white font-medium">{formatCost(row.cost)}</td>
      </tr>
      {open && (
        <tr className="bg-surface/60">
          <td />
          <td colSpan={11} className="px-3 py-3">
            <ChatExchange id={row.id} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/** One exchange: its question, answer and steps (also opened from "Run details" under a chat answer). */
export function ChatExchange({ id }: { id: number }) {
  const { data, isLoading, error } = useChatUsageDetails(id);
  if (isLoading) return <Loader2 className="w-4 h-4 animate-spin text-primary" />;
  if (error) return <p className="text-xs text-error">{(error as Error).message}</p>;
  if (!data) return null;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted mb-2">Question</p>
          <p className="text-xs text-white whitespace-pre-wrap break-words">{data.question ?? "—"}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted mb-2">
            Answer <span className="normal-case">· {data.answerChars.toLocaleString()} characters</span>
          </p>
          <div className={cn("max-h-[480px] overflow-auto text-xs pr-2", CUSTOM_SCROLLBAR)}>
            {data.answer ? <MarkdownRenderer content={data.answer} /> : <span className="text-muted">No answer.</span>}
          </div>
        </div>
      </div>
      <Steps trace={data.trace} />
    </div>
  );
}

/** The steps taken to answer, in order; deep analysis grouped by its parts (functional, design, …, summary). */
function Steps({ trace }: { trace: ChatUsageRow["trace"] }) {
  if (!trace) {
    return <p className="text-xs text-muted">No steps recorded for this chat (asked before steps were recorded).</p>;
  }
  return <StepGroups title="Steps" steps={trace.steps} truncated={trace.truncated} order={STAGE_ORDER} />;
}

const STAGE_ORDER = ["functional", "architecture", "design", "code", "summary"];
